import React, { useEffect } from 'react'
import { Card, Table, Button, Row, Col } from 'react-bootstrap'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import 'chartjs-adapter-date-fns'
import { Line } from 'react-chartjs-2'

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
)

// Define emotion types mapping
const EMOTION_TYPES = {
  0: { label: '很棒', value: 4, color: 'rgb(75, 192, 192)' },
  1: { label: '開心', value: 3, color: 'rgb(54, 162, 235)' },
  2: { label: '還行', value: 2, color: 'rgb(255, 206, 86)' },
  3: { label: '不開心', value: 1, color: 'rgb(255, 159, 64)' },
  4: { label: '很糟', value: 0, color: 'rgb(255, 99, 132)' },
}

// Utility function to safely parse date
const safeParseDate = (dateString) => {
  try {
    const date = new Date(dateString)
    return isNaN(date.getTime()) ? null : date
  } catch {
    return null
  }
}

// Utility function to validate record
const isValidRecord = (record) => {
  return (
    record &&
    typeof record === 'object' &&
    record.record_time &&
    safeParseDate(record.record_time) &&
    typeof record.emotion_type !== 'undefined'
  )
}

const EmotionRecord = ({
  data,
  startDate,
  endDate,
  setShowAddForm,
  onAddRecord,
}) => {
  // Process chart data
  const processChartData = () => {
    if (!startDate || !endDate || !Array.isArray(data)) {
      return { labels: [], datasets: [] }
    }

    // 生成日期範圍
    const dateSet = new Set()
    const start = new Date(startDate)
    const end = new Date(endDate)
    const currentDate = new Date(start)

    while (currentDate <= end) {
      dateSet.add(currentDate.toISOString().split('T')[0])
      currentDate.setDate(currentDate.getDate() + 1)
    }

    const allDates = Array.from(dateSet).sort()

    // 格式化日期標籤
    const formatDateTime = (dateStr) => {
      const date = new Date(dateStr)
      return `${date.getMonth() + 1}/${date.getDate()}`
    }

    // 處理記錄，一天可以有多個點
    const validRecords = data.filter(isValidRecord)
    const points = validRecords.map((record) => {
      const date = record.record_time.split(' ')[0]
      const emotionType = record.emotion_type
      return {
        x: formatDateTime(date),
        y: EMOTION_TYPES[emotionType].value,
        emotionType,
        note: record.note || '',
        comment: record.comment || '',
        recordTime: record.record_time,
      }
    })

    return {
      labels: allDates.map(formatDateTime),
      datasets: [
        {
          label: '心情記錄',
          data: points,
          backgroundColor: points.map(
            (point) => EMOTION_TYPES[point.emotionType].color
          ),
          borderColor: 'rgba(0, 0, 0, 0)', // 透明的線條，實現不連線的效果
          pointRadius: 8, // 點的大小
          pointHoverRadius: 10, // 滑鼠懸停時點的大小
          showLine: false, // 不顯示連線
        },
      ],
    }
  }

  // 圖表配置
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        min: -1,
        max: 4,
        ticks: {
          // 修改這裡以確保顯示所有值
          stepSize: 1,
          callback: function (value) {
            // 改用整數值來匹配
            const intValue = Math.round(value - 1)
            const emotion = Object.values(EMOTION_TYPES).find(
              (e) => e.value - 1 === intValue
            )
            return emotion ? emotion.label : ''
          },
          font: {
            size: 14,
          },
          padding: 10,
          autoSkip: false, // 添加這行以防止跳過刻度
        },
        grid: {
          color: '#CCCCCC',
          borderDash: [2, 2],
        },
        beginAtZero: true, // 添加這行確保從 0 開始
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 0, // 防止標籤旋轉
          autoSkip: true, // 自動跳過重疊的標籤
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const point = context.raw
            const emotion = EMOTION_TYPES[point.emotionType].label
            const time = new Date(point.recordTime).toLocaleTimeString()
            return [
              `心情：${emotion}`,
              `時間：${time}`,
              `心情日記：${point.note || '-'}`,
            ]
          },
        },
      },
    },
  }
  // Group data by date for table display
  const groupDataByDate = (records) => {
    if (!startDate || !endDate || !Array.isArray(records))
      return { datasets: [] }

    // Generate date range
    const dateSet = new Set()
    const start = new Date(startDate)
    const end = new Date(endDate)
    const currentDate = new Date(start)

    while (currentDate <= end) {
      dateSet.add(currentDate.toISOString().split('T')[0])
      currentDate.setDate(currentDate.getDate() + 1)
    }

    // Convert to array and sort (descending, newest first)
    let allDates = Array.from(dateSet).sort((a, b) => new Date(b) - new Date(a))

    // Create date to records mapping
    const recordsByDate = records
      .filter(isValidRecord)
      .reduce((acc, record) => {
        const date = record.record_time.split(' ')[0]
        if (!acc[date]) acc[date] = []
        acc[date].push(record)
        return acc
      }, {})

    return allDates.map((date) => ({
      date,
      records: (recordsByDate[date] || []).sort(
        (a, b) => new Date(b.record_time) - new Date(a.record_time)
      ),
    }))
  }

  // Format time
  const formatTime = (dateTimeStr) => {
    const date = safeParseDate(dateTimeStr)
    if (!date) return '-'
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
  }

  // Format date
  const formatDate = (dateStr) => {
    const date = safeParseDate(dateStr)
    if (!date) return dateStr
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    return `${dateStr} ${weekdays[date.getDay()]}`
  }

  const groupedData = groupDataByDate(data || [])

  return (
    <Row className="emotion-record g-3">
      {/* Chart Section */}
      <Col xs={12} lg={6}>
        <Card className="responsive-card">
          <Card.Body>
            <Card.Title className="d-flex justify-content-end align-items-center mb-4">
              <div className="ms-3 d-flex gap-3">
                {Object.values(EMOTION_TYPES).map((emotion, index) => (
                  <span key={index}>
                    <i
                      className="fas fa-circle me-1"
                      style={{ color: emotion.color }}
                    ></i>
                    {emotion.label}
                  </span>
                ))}
              </div>
            </Card.Title>
            <div className="responsive-chart">
              <Line options={chartOptions} data={processChartData()} />
            </div>
          </Card.Body>
        </Card>
      </Col>

      {/* Table Section */}
      <Col xs={12} lg={6}>
        <Card className="responsive-card">
          <Card.Body>
            <div className="table-responsive">
              {groupedData.map((dayGroup) => (
                <Table
                  key={dayGroup.date}
                  hover
                  className="mb-0 border rounded-3 overflow-hidden"
                >
                  <thead>
                    <tr className="table-light">
                      <th colSpan="3">{formatDate(dayGroup.date)}</th>
                      <th className="text-end">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="px-3"
                          onClick={() => {
                            setShowAddForm(true)
                            onAddRecord?.(dayGroup.date)
                          }}
                        >
                          <i className="fa-solid fa-plus me-2"></i>
                          新增
                        </Button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {dayGroup.records.length > 0 ? (
                      dayGroup.records.map((record) => (
                        <tr key={record.action_id}>
                          <td>{formatTime(record.record_time)}</td>
                          <td>{EMOTION_TYPES[record.emotion_type].label}</td>
                          <td>{record.note || '-'}</td>
                          <td>{record.comment || '-'}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center text-muted">
                          尚無心情紀錄
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              ))}
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  )
}

export default EmotionRecord
